<script setup lang="ts">
import AppButton from '@/application/components/ui/AppButton.vue';
import { ElAlert } from 'element-plus';
import { NotificationVariant, useNotification } from '@/application/components/ui/useNotification';
import { updateBankAccount } from '@/api/bankAccount/bankAccountsApi';
import { useDetectMobile } from '@/application/composables/detectMobile';
import type { BankAccount, BankAccountContract } from '@/api/bankAccount/types';
import { useUserDataStore } from '@/user/store/userDataStore';
import { ref } from 'vue';
import SelectContracts from '@/user/components/SelectContracts.vue';
import ContractsField from '@/user/components/ContractsField.vue';
import { TrackEventCategory } from '@/application/utils/tracking/events';
import { useForm } from "@/api/lib/integration";
import { useTagManager } from '@demv_systems/feu-tag-manager';
import { ElForm, ElFormItem, ElInput, ElSwitch } from 'element-plus';

interface Props {
  bankAccount: BankAccount
}

const props = defineProps<Props>();
const emit = defineEmits(['close']);
const showSelectContracts = ref(false);

const { isMobile } = useDetectMobile();
const { fetchBankAccounts } = useUserDataStore();
const { show: showNotification } = useNotification();

const { form, submit, reset, errors, errorMsg } = useForm(
    updateBankAccount,
    {
      data: {
        id: props.bankAccount.id,
        depositor: props.bankAccount.depositor,
        note: props.bankAccount.note,
        main: props.bankAccount.main,
        contractIds: props.bankAccount.contractIds,
      },
      previous: {
        id: props.bankAccount.id,
        depositor: props.bankAccount.depositor,
        note: props.bankAccount.note,
        main: props.bankAccount.main,
        contractIds: props.bankAccount.contractIds,
      },
      additional: {
        contracts: [] as BankAccountContract[],
      },
    },
    async () => {
      showNotification('Ihre Daten wurden erfolgreich gespeichert.', NotificationVariant.Success);
      useTagManager().push({ event: TrackEventCategory.BANK_ACCOUNT, action: 'bearbeiten' })
      await fetchBankAccounts();
      close();
    },
  );

function close() {
  reset();
  showSelectContracts.value = false;
  emit('close');
}

async function handleEditAccount() {
  form.value.additional.contracts = useUserDataStore()
    .getBankAccountContractByIds([...form.value.data.contractIds, ...props.bankAccount.contractIds]);
  await submit();
}
</script>

<template>
  <div class="flex flex-col h-full justify-between">
    <Transition name="fade" mode="out-in">
      <ElForm
        v-if="!showSelectContracts"
        require-asterisk-position="right"
        @submit.prevent
      >
        <div class="flex flex-col gap-4 pt-6 pb-2">
          <ElFormItem
            label="Kontoinhaber"
            for="Kontoinhaber"
            :label-position="isMobile ? 'top' : 'left'"
            class="whitespace-nowrap text-start md:text-end"
            :error="errors.depositor?.[0]"
          >
            <div class="w-full flex justify-end">
              <ElInput
                id="Kontoinhaber"
                v-model="form.data.depositor"
                placeholder=""
                class="w-full md:w-80"
              />
            </div>
          </ElFormItem>

          <ElFormItem
            label="Bezeichnung"
            for="note"
            :label-position="isMobile ? 'top' : 'left'"
            class="whitespace-nowrap text-start md:text-end"
            :error="errors.note?.[0]"
          >
            <div class="w-full flex justify-end">
              <ElInput
                id="note"
                v-model="form.data.note"
                placeholder=""
                class="w-full md:w-80"
              />
            </div>
          </ElFormItem>

          <ElFormItem
            label="Standard"
            :label-position="isMobile ? 'top' : 'left'"
            class="whitespace-nowrap"
          >
            <div class="w-full flex md:justify-end py-1">
              <ElSwitch
                v-model="form.data.main"
                class="md:mr-[272px]"
                size="large"
                aria-label="Standardbankverbindung"
              />
            </div>
          </ElFormItem>

          <ContractsField
            :model-value="form.data.contractIds"
            @show-select-contracts="showSelectContracts = !showSelectContracts"
          />
        </div>
      </ElForm>
      <SelectContracts
        v-else
        v-model="form.data.contractIds"
        :bank-account-id="bankAccount.id"
        @close="showSelectContracts = false"
      />
    </Transition>

    <ElAlert
      v-if="errorMsg"
      :title="errorMsg"
      type="error"
      class="my-5"
      show-icon
    />

    <AppButton
      class="sticky bottom-0 mt-auto sm:ml-auto"
      type="primary"
      :handler="handleEditAccount"
    >
      Speichern
    </AppButton>
  </div>
</template>
